import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Pencil, Trash2, PlusCircle, MailOpen, Inbox, ShieldCheck, ShieldAlert, MessageCircle, CalendarCheck2, PackageCheck, PackageX, Activity, ChevronRight, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatApiErrorDetail } from "@/lib/api";
import VerifiedBadge from "@/components/locanest/VerifiedBadge";
import ChatDrawer from "@/components/locanest/ChatDrawer";
import AntiOfflineNotice from "@/components/locanest/AntiOfflineNotice";
import TierBadge from "@/components/locanest/TierBadge";
import BankDetailsSection from "@/components/locanest/BankDetailsSection";

const TABS = ["listings", "bookings", "inquiries", "activity", "chats"];

export default function OwnerDashboard() {
  const { user, refresh } = useAuth();
  const [props, setProps] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [chats, setChats] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [activity, setActivity] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [params, setParams] = useSearchParams();
  const tabsRef = useRef(null);
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "listings";

  const goTab = (t) => {
    setParams({ tab: t });
    setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const load = () => {
    api.get("/properties", { params: { owner_id: user.id, limit: 200 } }).then(({ data }) => setProps(data));
    api.get("/inquiries/received").then(({ data }) => setInquiries(data));
    api.get("/chats").then(({ data }) => setChats(data)).catch(() => {});
    api.get("/bookings/mine").then(({ data }) => setBookings(Array.isArray(data) ? data : (data.items || []))).catch(() => {});
    api.get("/owner/activity", { params: { days: 7 } }).then(({ data }) => setActivity(data)).catch(() => {});
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const remove = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Listing removed");
    load();
  };

  const toggleAvail = async (p) => {
    const next = !p.is_available;
    try {
      await api.patch(`/properties/${p.id}/availability`, { is_available: next });
      toast.success(next ? "Listing is available again" : "Listing marked out of stock");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      {/* Verification banner */}
      {user?.verification_status !== "approved" && (
        <div className={`mb-6 rounded-lg border p-4 flex items-center gap-3 flex-wrap ${user?.verification_status === "pending" ? "border-[#C85A32]/30 bg-[#F0EBE1]" : user?.verification_status === "rejected" ? "border-red-300 bg-red-50" : "border-[#2B3A67]/25 bg-[#D4DEC6]/25"}`} data-testid="verify-banner">
          {user?.verification_status === "pending" ? <ShieldAlert className="text-[#C85A32]" /> : <ShieldCheck className="text-[#2B3A67]" />}
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold">
              {user?.verification_status === "pending" ? "Verification pending admin review"
                : user?.verification_status === "rejected" ? "Verification rejected — please resubmit"
                : "Get the Verified badge to build tenant trust"}
            </div>
            <div className="text-sm text-[#5C6B7A]">
              Commercial listings (Shop, Factory, Cafe, Warehouse) get a "Business verified" badge once your GST / Shop-Act proof is approved — not required to publish.
            </div>
          </div>
          <Button asChild variant="outline" className="rounded-md" data-testid="verify-cta">
            <Link to="/owner/verify">{user?.verification_status === "none" ? "Start verification" : "View status"}</Link>
          </Button>
        </div>
      )}
      {user?.verification_status === "approved" && (
        <div className="mb-6 rounded-lg border border-[#2B3A67]/25 bg-[#D4DEC6]/25 p-4 flex items-center gap-3" data-testid="verified-banner">
          <VerifiedBadge />
          <div className="text-sm">You're a verified owner — badge appears on all your listings.</div>
        </div>
      )}

      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Owner workspace</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Hi {user?.name?.split(" ")[0]} 👋</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <TierBadge tier={user?.vendor_tier || "new"} />
            {user?.rating_owner_count > 0 && (
              <span className="text-xs text-[#5C6B7A]" data-testid="owner-rating-summary">
                ★ {user.rating_owner_avg.toFixed(1)} · {user.rating_owner_count} review{user.rating_owner_count > 1 ? "s" : ""} · {user.completed_deals_count || 0} completed deal{user.completed_deals_count === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <p className="text-[#5C6B7A] mt-2">Manage your listings and tenant inquiries.</p>
        </div>
        <Button asChild className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="add-property-btn">
          <Link to="/owner/new"><PlusCircle size={16} className="mr-1.5" /> New listing</Link>
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="owner-stats">
        <Stat label="My listings" v={props.length} onClick={() => goTab("listings")} active={tab === "listings"} testId="stat-listings" hint="View & manage listings" />
        <Stat label="Total inquiries" v={inquiries.length} onClick={() => goTab("inquiries")} active={tab === "inquiries"} testId="stat-inquiries" hint="Open inquiries list" />
        <Stat label="New (last 7d)" v={activity ? activity.events.length : "…"} onClick={() => goTab("activity")} active={tab === "activity"} testId="stat-new-7d" hint="Open recent activity" />
      </div>

      <div className="mt-6"><AntiOfflineNotice tone="warn" /></div>

      <ProfileCompletionCard user={user} />
      <BankDetailsSection user={user} onSaved={refresh} />

      <Tabs value={tab} onValueChange={goTab} className="mt-10" ref={tabsRef}>
        <TabsList className="w-full justify-start overflow-x-auto h-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsTrigger value="listings" data-testid="tab-listings">Listings</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings {bookings.filter(b => b.status === "advance_paid").length > 0 && `(${bookings.filter(b => b.status === "advance_paid").length})`}</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Recent (7d) {activity?.events?.length > 0 && `(${activity.events.length})`}</TabsTrigger>
          <TabsTrigger value="chats" data-testid="tab-chats">Chats {chats.length > 0 && `(${chats.length})`}</TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-6">
          {props.length === 0 ? (
            <div className="bg-[#F0EBE1] rounded-lg p-10 text-center">
              <p className="text-[#5C6B7A]">No listings yet.</p>
              <Button asChild className="mt-4 bg-[#C85A32] hover:bg-[#a8482a] text-white"><Link to="/owner/new">Create your first listing</Link></Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="owner-listings-grid">
              {props.map((p) => (
                <div key={p.id} className="bg-[#F0EBE1] rounded-lg overflow-hidden border border-[#1A1F2C]/10 flex">
                  <div className="relative w-36 h-36 shrink-0">
                    <img src={p.photos?.[0]} alt="" className={`w-full h-full object-cover ${p.is_available === false ? "grayscale opacity-70" : ""}`} />
                    {p.is_available === false && (
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] rounded bg-[#C85A32] text-white" data-testid={`badge-oos-${p.id}`}>Out</span>
                    )}
                  </div>
                  <div className="flex-1 p-4 flex flex-col justify-between">
                    <div>
                      <Link to={`/property/${p.id}`} className="font-display text-lg hover:text-[#C85A32]">{p.title}</Link>
                      <div className="text-xs text-[#5C6B7A] mt-1">{p.locality}, {p.city} • {p.rent ? inr(p.rent) + "/mo" : (p.category_fields?.price_per_night ? inr(p.category_fields.price_per_night) + "/night" : "—")}</div>
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Button asChild variant="outline" size="sm" className="rounded-md"><Link to={`/owner/edit/${p.id}`}><Pencil size={12} className="mr-1" />Edit</Link></Button>
                      <Button onClick={() => toggleAvail(p)} variant="outline" size="sm" className={`rounded-md ${p.is_available === false ? "text-[#2B3A67]" : "text-[#C85A32]"}`} data-testid={`toggle-avail-${p.id}`}>
                        {p.is_available === false ? <><PackageCheck size={12} className="mr-1" />Mark available</> : <><PackageX size={12} className="mr-1" />Out of stock</>}
                      </Button>
                      <Button onClick={() => remove(p.id)} variant="outline" size="sm" className="rounded-md text-red-600 hover:text-red-700" data-testid={`delete-${p.id}`}><Trash2 size={12} className="mr-1" />Delete</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bookings" className="mt-6">
          {bookings.length === 0 ? (
            <div className="bg-[#F0EBE1] rounded-lg p-10 text-center text-[#5C6B7A]">
              <CalendarCheck2 className="mx-auto opacity-50" /> No bookings yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="owner-bookings-list">
              {bookings.map((b) => (
                <Link key={b.id} to={`/bookings/${b.id}`} data-testid={`owner-booking-${b.id}`} className="flex items-center justify-between gap-4 bg-[#F0EBE1] rounded-lg p-5 border border-[#1A1F2C]/10 hover:border-[#C85A32]/40">
                  <div className="min-w-0">
                    <div className="font-display text-lg">{b.property_title}</div>
                    <div className="text-xs text-[#5C6B7A]">from {b.tenant_name} · Agr {b.agreement_number}</div>
                    <div className="text-sm mt-1">{inr(b.amount_paise / 100)}{b.check_in ? ` · ${b.check_in} → ${b.check_out}` : ""}</div>
                  </div>
                  <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-[0.16em] ${b.status === "advance_paid" ? "bg-[#D4DEC6] text-[#2B3A67]" : b.status === "completed" ? "bg-[#D4DEC6] text-[#2B3A67]" : b.status === "pending" ? "bg-[#F0EBE1] text-[#C85A32]" : "bg-red-50 text-red-600"}`}>{b.status.replace("_", " ")}</span>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inquiries" className="mt-6">
          {inquiries.length === 0 ? (
            <div className="bg-[#F0EBE1] rounded-lg p-10 text-center text-[#5C6B7A]">
              <Inbox className="mx-auto opacity-50" /> No inquiries yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="inquiries-list">
              {inquiries.map((i) => (
                <div key={i.id} className="bg-[#F0EBE1] rounded-lg p-5 border border-[#1A1F2C]/10">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-display text-lg flex items-center gap-2 flex-wrap">
                        {i.tenant_name}
                        {i.source === "interest" && <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[#C85A32]/15 text-[#C85A32] font-semibold" data-testid={`inquiry-interest-badge-${i.id}`}>Interest · Reserve request</span>}
                      </div>
                      <div className="text-xs text-[#5C6B7A]">{new Date(i.created_at).toLocaleString()}</div>
                    </div>
                    <Link to={`/property/${i.property_id}`} className="text-xs text-[#C85A32]">{i.property_title}</Link>
                  </div>
                  <p className="mt-3 text-sm">{i.message}</p>
                  {i.visit_date && <div className="mt-2 text-xs text-[#2B3A67]">Wants to visit: {i.visit_date}</div>}
                  <div className="mt-3 text-sm text-[#5C6B7A] flex items-center gap-3 flex-wrap">
                    <a href={`mailto:${i.tenant_email}`} className="hover:text-[#C85A32] inline-flex items-center gap-1"><MailOpen size={12} /> {i.tenant_email}</a>
                    {i.tenant_phone && <a href={`tel:+91${String(i.tenant_phone).replace(/\D/g, "").slice(-10)}`} className="hover:text-[#C85A32] inline-flex items-center gap-1"><Phone size={12} /> {i.tenant_phone}</a>}
                    {i.booking_id && <Link to={`/bookings/${i.booking_id}`} className="hover:text-[#C85A32] inline-flex items-center gap-1 underline" data-testid={`inquiry-booking-link-${i.id}`}><CalendarCheck2 size={12} /> View reservation</Link>}
                    {i.chat_id && (
                      <Button size="sm" onClick={() => setChatId(i.chat_id)} className="ml-auto bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md" data-testid={`inquiry-reply-${i.id}`}>
                        <MessageCircle size={12} className="mr-1" /> Reply in chat
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          {!activity ? (
            <div className="text-sm text-[#5C6B7A]">Loading…</div>
          ) : activity.events.length === 0 ? (
            <div className="bg-[#F0EBE1] rounded-lg p-10 text-center text-[#5C6B7A]" data-testid="activity-empty">
              <Activity className="mx-auto opacity-50" /> Nothing new in the last 7 days.
            </div>
          ) : (
            <div data-testid="activity-list">
              <div className="text-xs text-[#5C6B7A] mb-3" data-testid="activity-counts">
                Last 7 days: {activity.counts.inquiries} inquiries · {activity.counts.chats} chats · {activity.counts.bookings} reservations
              </div>
              <div className="space-y-2">
                {activity.events.map((e) => (
                  <div key={`${e.type}-${e.id}`} className="bg-[#F0EBE1] rounded-lg p-4 border border-[#1A1F2C]/10 flex items-center gap-3" data-testid={`activity-${e.type}-${e.id}`}>
                    <span className={`shrink-0 w-9 h-9 rounded-full grid place-items-center ${e.type === "inquiry" ? "bg-[#C85A32]/15 text-[#C85A32]" : e.type === "chat" ? "bg-[#2B3A67]/15 text-[#2B3A67]" : "bg-[#D4DEC6] text-[#2B3A67]"}`}>
                      {e.type === "inquiry" ? <Inbox size={16} /> : e.type === "chat" ? <MessageCircle size={16} /> : <CalendarCheck2 size={16} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{e.title}</div>
                      <div className="text-xs text-[#5C6B7A] truncate">{e.subtitle} · {new Date(e.at).toLocaleString()}</div>
                      {e.message && <div className="text-xs mt-1 line-clamp-2">{e.message}</div>}
                    </div>
                    {e.chat_id ? (
                      <Button size="sm" variant="outline" onClick={() => setChatId(e.chat_id)} className="rounded-md" data-testid={`activity-open-chat-${e.id}`}>Open chat <ChevronRight size={12} /></Button>
                    ) : e.booking_id ? (
                      <Button asChild size="sm" variant="outline" className="rounded-md"><Link to={`/bookings/${e.booking_id}`}>View <ChevronRight size={12} /></Link></Button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="chats" className="mt-6">
          {chats.length === 0 ? (
            <div className="bg-[#F0EBE1] rounded-lg p-10 text-center text-[#5C6B7A]">
              <MessageCircle className="mx-auto opacity-50" /> No chats yet. Tenants will appear here when they message you.
            </div>
          ) : (
            <div className="space-y-3" data-testid="chats-list">
              {chats.map((c) => (
                <button key={c.id} onClick={() => setChatId(c.id)} className="w-full text-left bg-[#F0EBE1] rounded-lg p-4 border border-[#1A1F2C]/10 hover:border-[#C85A32]/40 transition" data-testid={`chat-item-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-display text-lg">{c.tenant_name}</div>
                      <div className="text-xs text-[#5C6B7A]">re: {c.property_title}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-[#5C6B7A]">{new Date(c.last_at).toLocaleString()}</div>
                      {c.unread_owner > 0 && <span className="inline-block mt-1 px-2 py-0.5 bg-[#C85A32] text-white text-[10px] rounded-full">{c.unread_owner} new</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ChatDrawer chatId={chatId} open={!!chatId} onOpenChange={(v) => !v && setChatId(null)} onOpened={load} />
    </div>
  );
}

function Stat({ label, v, onClick, testId, active, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left bg-[#F0EBE1] border rounded-lg p-5 hover:border-[#C85A32]/40 hover:shadow-md transition-all cursor-pointer ${active ? "border-[#C85A32]" : "border-[#1A1F2C]/10"}`}
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-[0.2em] text-[#5C6B7A]">{label}</div>
      <div className="font-display text-3xl mt-1 text-[#C85A32]">{v}</div>
      <div className="text-[11px] text-[#2B3A67] mt-1 inline-flex items-center gap-0.5">{hint} <ChevronRight size={11} /></div>
    </button>
  );
}

const MISSING_SECTION = { "Verification approved": "verification", "Business name": "details", "Bank details": "bank", "Phone": "details", "Name": "details" };

function ProfileCompletionCard({ user }) {
  const pc = user?.profile_completion;
  if (!pc || pc.percent === 100) return null;
  const first = pc.missing?.[0];
  const target = `/profile?section=${MISSING_SECTION[first] || "details"}`;
  return (
    <Link to={target} className="block mt-6 rounded-lg bg-[#F0EBE1] border border-[#2B3A67]/20 p-5 hover:border-[#C85A32]/50 hover:shadow-md transition-all" data-testid="profile-completion-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#2B3A67] font-semibold">Complete your profile</div>
          <p className="text-sm text-[#1A1F2C] mt-1">Your profile is <strong>{pc.percent}%</strong> complete — {pc.filled}/{pc.total} sections done.</p>
        </div>
        <div className="w-full sm:w-64">
          <div className="h-2 bg-[#FDFBF7] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#C85A32] transition-all duration-500"
              style={{ width: `${pc.percent}%` }}
              data-testid="profile-completion-bar"
            />
          </div>
        </div>
      </div>
      {pc.missing?.length > 0 && (
        <div className="mt-3 text-xs text-[#5C6B7A]">
          Still to do: <span className="text-[#1A1F2C]">{pc.missing.join(" · ")}</span>
          <span className="ml-2 text-[#C85A32] font-medium inline-flex items-center gap-0.5">Complete now <ChevronRight size={11} /></span>
        </div>
      )}
    </Link>
  );
}
